import {
  IBlueprintBuilder,
  IBlueprint,
  BlueprintType,
  BlueprintCategory,
  BlueprintParameter,
  BlueprintParameterGroup,
  BlueprintMetadata,
  BlueprintGenerationContext,
  BlueprintGenerationResult
} from '../types/blueprint-interfaces';
import { Blueprint } from './Blueprint';

/**
 * Построитель шаблонов для удобного создания Blueprint
 */
export class BlueprintBuilder implements IBlueprintBuilder {
  private basicInfo?: {
    id: string;
    name: string;
    description: string;
    type: BlueprintType;
    category: BlueprintCategory;
  };

  private version: string = '1.0.0';
  private metadata: BlueprintMetadata = {};
  private parameterGroups: BlueprintParameterGroup[] = [];
  private generator?: (context: BlueprintGenerationContext) => Promise<BlueprintGenerationResult>;

  /**
   * Установка базовой информации
   */
  setBasicInfo(info: {
    id: string;
    name: string;
    description: string;
    type: BlueprintType;
    category: BlueprintCategory;
  }): IBlueprintBuilder {
    this.basicInfo = info;
    return this;
  }

  /**
   * Установка версии
   */
  setVersion(version: string): IBlueprintBuilder {
    this.version = version;
    return this;
  }

  /**
   * Добавление группы параметров
   */
  addParameterGroup(group: BlueprintParameterGroup): IBlueprintBuilder {
    // Проверяем что группа с таким ID еще не существует
    const existingIndex = this.parameterGroups.findIndex(g => g.id === group.id);
    if (existingIndex !== -1) {
      throw new Error(`Группа параметров с ID '${group.id}' уже существует`);
    }

    this.parameterGroups.push({ ...group });
    return this;
  }

  /**
   * Добавление параметра
   */
  addParameter(parameter: BlueprintParameter, groupId?: string): IBlueprintBuilder {
    // Проверяем что параметр с таким ID еще не существует
    const existingParam = this.parameterGroups
      .flatMap(g => g.parameters)
      .find(p => p.id === parameter.id);
    
    if (existingParam) {
      throw new Error(`Параметр с ID '${parameter.id}' уже существует`);
    }

    if (groupId) {
      // Добавляем в указанную группу
      const group = this.parameterGroups.find(g => g.id === groupId);
      if (!group) {
        throw new Error(`Группа параметров с ID '${groupId}' не найдена`);
      }
      group.parameters.push({ ...parameter });
    } else {
      // Создаем группу по умолчанию если ее нет
      let defaultGroup = this.parameterGroups.find(g => g.id === 'default');
      if (!defaultGroup) {
        defaultGroup = {
          id: 'default',
          name: 'Основные параметры',
          description: 'Основные параметры шаблона',
          parameters: []
        };
        this.parameterGroups.push(defaultGroup);
      }
      defaultGroup.parameters.push({ ...parameter });
    }

    return this;
  }

  /**
   * Добавление нескольких параметров
   */
  addParameters(parameters: BlueprintParameter[], groupId?: string): IBlueprintBuilder {
    for (const param of parameters) {
      this.addParameter(param, groupId);
    }
    return this;
  }

  /**
   * Создание и добавление группы с параметрами
   */
  addGroupWithParameters(
    groupInfo: Omit<BlueprintParameterGroup, 'parameters'>,
    parameters: BlueprintParameter[]
  ): IBlueprintBuilder {
    const group: BlueprintParameterGroup = {
      ...groupInfo,
      parameters: [...parameters]
    };
    return this.addParameterGroup(group);
  }

  /**
   * Установка метаданных
   */
  setMetadata(metadata: BlueprintMetadata): IBlueprintBuilder {
    this.metadata = { ...this.metadata, ...metadata };
    return this;
  }

  /**
   * Добавление тегов
   */
  addTags(tags: string[]): IBlueprintBuilder {
    this.metadata.tags = [...(this.metadata.tags || []), ...tags];
    return this;
  }

  /**
   * Добавление примера использования
   */
  addExample(example: {
    name: string;
    description: string;
    parameters: Record<string, any>;
  }): IBlueprintBuilder {
    if (!this.metadata.examples) {
      this.metadata.examples = [];
    }
    this.metadata.examples.push(example);
    return this;
  }

  /**
   * Установка автора
   */
  setAuthor(author: string): IBlueprintBuilder {
    this.metadata.author = author;
    return this;
  }

  /**
   * Установка лицензии
   */
  setLicense(license: string): IBlueprintBuilder {
    this.metadata.license = license;
    return this;
  }

  /**
   * Установка документации
   */
  setDocumentation(documentation: string): IBlueprintBuilder {
    this.metadata.documentation = documentation;
    return this;
  }

  /**
   * Установка генератора
   */
  setGenerator(
    generator: (context: BlueprintGenerationContext) => Promise<BlueprintGenerationResult>
  ): IBlueprintBuilder {
    this.generator = generator;
    return this;
  }

  /**
   * Построение шаблона
   */
  build(): IBlueprint {
    const validation = this.validate();
    if (!validation.valid) {
      throw new Error(`Ошибки валидации шаблона: ${validation.errors.join(', ')}`);
    }

    return new Blueprint({
      id: this.basicInfo!.id,
      name: this.basicInfo!.name,
      description: this.basicInfo!.description,
      type: this.basicInfo!.type,
      category: this.basicInfo!.category,
      version: this.version,
      metadata: {
        ...this.metadata,
        created: this.metadata.created || new Date(),
        updated: new Date()
      },
      parameterGroups: this.parameterGroups,
      generator: this.generator!
    });
  }

  /**
   * Валидация конфигурации
   */
  validate(): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Проверка базовой информации
    if (!this.basicInfo) {
      errors.push('Не установлена базовая информация шаблона');
    } else {
      if (!this.basicInfo.id.trim()) {
        errors.push('ID шаблона не может быть пустым');
      }
      if (!this.basicInfo.name.trim()) {
        errors.push('Название шаблона не может быть пустым');
      }
      if (!this.basicInfo.description.trim()) {
        errors.push('Описание шаблона не может быть пустым');
      }
    }

    // Проверка генератора
    if (!this.generator) {
      errors.push('Не установлен генератор шаблона');
    }

    // Проверка параметров
    const allParameterIds = new Set<string>();
    for (const group of this.parameterGroups) {
      if (!group.id.trim()) {
        errors.push('ID группы параметров не может быть пустым');
      }
      if (!group.name.trim()) {
        errors.push(`Название группы '${group.id}' не может быть пустым`);
      }

      for (const param of group.parameters) {
        if (!param.id.trim()) {
          errors.push(`ID параметра в группе '${group.id}' не может быть пустым`);
        }
        if (!param.name.trim()) {
          errors.push(`Название параметра '${param.id}' не может быть пустым`);
        }
        
        // Проверка уникальности ID параметров
        if (allParameterIds.has(param.id)) {
          errors.push(`Дублирующийся ID параметра: '${param.id}'`);
        }
        allParameterIds.add(param.id);

        // Проверка зависимостей параметров
        if (param.dependencies) {
          for (const depId of param.dependencies) {
            if (!allParameterIds.has(depId)) {
              errors.push(`Параметр '${param.id}' зависит от несуществующего параметра '${depId}'`);
            }
          }
        }

        // Проверка опций для select/multiselect
        if ((param.type === 'select' || param.type === 'multiselect') && !param.options?.length) {
          errors.push(`Параметр '${param.id}' типа ${param.type} должен иметь опции`);
        }

        // Проверка валидации
        if (param.validation) {
          const val = param.validation;
          if (val.min !== undefined && val.max !== undefined && val.min > val.max) {
            errors.push(`Параметр '${param.id}': минимальное значение больше максимального`);
          }
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Сброс построителя
   */
  reset(): IBlueprintBuilder {
    this.basicInfo = undefined;
    this.version = '1.0.0';
    this.metadata = {};
    this.parameterGroups = [];
    this.generator = undefined;
    return this;
  }

  /**
   * Клонирование существующего шаблона для модификации
   */
  static fromBlueprint(blueprint: IBlueprint): BlueprintBuilder {
    const builder = new BlueprintBuilder();
    
    builder.setBasicInfo({
      id: blueprint.id,
      name: blueprint.name,
      description: blueprint.description,
      type: blueprint.type,
      category: blueprint.category
    });

    builder.setVersion(blueprint.version);
    builder.setMetadata(blueprint.metadata);

    for (const group of blueprint.parameterGroups) {
      builder.addParameterGroup(group);
    }

    return builder;
  }

  /**
   * Создание быстрого шаблона с минимальными настройками
   */
  static quickBlueprint(
    id: string,
    name: string,
    description: string,
    type: BlueprintType,
    category: BlueprintCategory,
    generator: (context: BlueprintGenerationContext) => Promise<BlueprintGenerationResult>
  ): IBlueprint {
    return new BlueprintBuilder()
      .setBasicInfo({ id, name, description, type, category })
      .setGenerator(generator)
      .build();
  }
} 